import React, { useState, useEffect } from 'react';
import {
  signInWithPopup,
  signOut,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { PersonIcon, ExitIcon } from '@radix-ui/react-icons';
import { useNotifications } from './Notifications';
import { useI18n } from '../i18n';

/**
 * Controlled by App: the single onAuthStateChanged subscription lives there so the
 * whole app can hold its first render until Firebase has answered.
 */
const AuthComponent = ({ user, authResolved = true, compact = false }) => {
  const { t } = useI18n();
  const [signingIn, setSigningIn] = useState(false);
  const { notifyError } = useNotifications();

  useEffect(() => {
    // Completes the mobile redirect fallback below.
    getRedirectResult(auth).catch((error) => {
      console.error('Redirect sign-in error:', error);
    });
  }, []);

  const signInWithProvider = async (provider, providerName) => {
    try {
      setSigningIn(true);

      // Try popup first, fall back to redirect on mobile
      try {
        await signInWithPopup(auth, provider);
      } catch (popupError) {
        if (popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user') {
          // Fallback to redirect for mobile devices
          await signInWithRedirect(auth, provider);
        } else {
          throw popupError;
        }
      }
    } catch (error) {
      console.error(`Error signing in with ${providerName}:`, error);
      notifyError(`Could not sign in with ${providerName}. ${error?.message || 'Please try again.'}`);
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      notifyError(t('auth.signOutFailed'));
    }
  };

  if (!authResolved) {
    return <div className="auth-loading">{t('nav.loading')}</div>;
  }

  if (user) {
    return (
      <div className="auth-user-info">
        <div className="user-avatar">
          {user.photoURL ? (
            <img src={user.photoURL} alt="Profile" className="avatar-image" />
          ) : (
            <div className="avatar-placeholder">
              {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
        </div>
        <div className="user-details">
          <span className="user-name">{user.displayName || user.email}</span>
          <button onClick={handleSignOut} className="btn btn-secondary sign-out-btn">
            <ExitIcon style={{ marginRight: '4px', width: '12px', height: '12px' }} />
            {t('nav.signOut')}
          </button>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={() => signInWithProvider(googleProvider, 'Google')}
        className="btn auth-btn-compact"
        disabled={signingIn}
        title="Sign in with Google"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        {signingIn ? t('nav.signingIn') : t('nav.signIn')}
      </button>
    );
  }

  return (
    <div className="auth-signin">
      <div className="auth-icon-wrapper">
        <PersonIcon className="auth-main-icon" />
      </div>
      <h3>{t('auth.title')}</h3>
      <p>{t('auth.subtitle')}</p>

      <div className="auth-buttons">
        <button
          onClick={() => signInWithProvider(googleProvider, 'Google')}
          className="btn auth-btn google-btn"
          disabled={signingIn}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {signingIn ? t('nav.signingIn') : t('auth.google')}
        </button>
      </div>
    </div>
  );
};

export default AuthComponent;
